import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { unwrap } from '@/api/envelope';
import { readProfile } from '@/api/generated';
import { ROLE_LABELS, ROLES } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';
import { organizationProfileKey } from '@/features/account/keys';
import { testIdAttr, testIdPart } from '@/lib/testIds';

/**
 * The open organization's logo, for the people who may read it.
 *
 * The logo is only in the profile, and the profile is for a Super Admin and
 * for staff viewing the organization. An Admin would be refused, so for them
 * nothing is asked and the name is shown instead.
 *
 * The key carries the organization's id (see `organizationProfileKey`). The
 * Profile page writes each record it gets back from an upload, a removal or a
 * save into this key as well as its own, so the bar follows a new logo without
 * a reload.
 */
function useLogo(organizationId: string | null, mayRead: boolean) {
  const enabled = mayRead && organizationId !== null;
  const query = useQuery({
    queryKey: organizationProfileKey(organizationId),
    queryFn: async () => {
      const reply = await readProfile({ throwOnError: true });
      return unwrap(reply.data);
    },
    enabled,
  });
  return {
    // A disabled query counts as pending too, which is not waiting for anything.
    pending: enabled && query.isPending,
    url: query.data?.logo?.url ?? null,
  };
}

/**
 * The logo when there is one, the name when there is not.
 *
 * A logo that fails to load - a signed address that has expired, say - gives
 * way to the name rather than leaving a broken image in the bar. Remembering
 * which address failed, rather than just that one did, lets a fresh address
 * try again.
 */
function OrganizationMark({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  const [failed, setFailed] = useState<string | null>(null);

  if (logoUrl && logoUrl !== failed) {
    return (
      <Box
        component="img"
        src={logoUrl}
        // The name, so a screen reader hears what the name used to say.
        alt={name}
        onError={() => setFailed(logoUrl)}
        data-testid="org-switcher-logo-image"
        sx={{
          display: 'block',
          height: 40,
          width: 'auto',
          maxWidth: 180,
          objectFit: 'contain',
        }}
      />
    );
  }

  return (
    <Typography
      variant="subtitle1"
      component="span"
      noWrap
      data-testid="org-switcher-name-value"
      sx={{ fontWeight: 600 }}
    >
      {name}
    </Typography>
  );
}

/**
 * Which organization the portal is currently showing, and a way to change it.
 * It sits in the middle of the top bar, as the organization's logo or name.
 *
 * Only a button when somebody belongs to more than one. With a single
 * organization there is nothing to switch between, so it stays the plain
 * label it has always been rather than a menu that opens onto one item.
 *
 * Switching is more than a label change: the person's role travels with the
 * organization, so the menu shows the role held in each and the navigation
 * changes with the choice.
 */
export function OrganizationSwitcher() {
  const { user, switchOrganization } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const { viewing } = useViewingAs();

  // Staff viewing an organization see its name and nothing to switch to: they
  // belong to none, and the way out is the banner's Exit.
  const organizations = viewing ? [] : (user?.organizations ?? []);
  const current = viewing ? viewing.name : (user?.organizationName ?? '');
  const currentId = viewing ? viewing.id : (user?.organizationId ?? null);

  const logo = useLogo(currentId, viewing !== null || user?.role === ROLES.SUPER_ADMIN);

  // Staff in their own area are in no organization, so there is nothing to
  // name. While the logo is on its way the middle stays empty, rather than
  // showing the name and then swapping it for the logo a moment later.
  if (!current || logo.pending) return null;

  const mark = <OrganizationMark name={current} logoUrl={logo.url} />;

  if (organizations.length <= 1) {
    return (
      <Box
        data-testid="org-switcher"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          minWidth: 0,
        }}
      >
        {mark}
      </Box>
    );
  }

  const choose = (organizationId: string) => {
    switchOrganization(organizationId);
    setAnchor(null);
  };

  return (
    <>
      <Button
        size="small"
        color="inherit"
        onClick={(event) => setAnchor(event.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        aria-haspopup="menu"
        aria-expanded={anchor ? true : undefined}
        aria-label={`Current organization: ${current}. Switch organization`}
        data-testid="org-switcher-button"
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          minWidth: 0,
          textTransform: 'none',
        }}
      >
        {mark}
      </Button>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{
          list: { 'aria-label': 'Your organizations' },
          paper: testIdAttr('org-switcher-menu'),
        }}
      >
        {organizations.map((organization) => (
          <MenuItem
            key={organization.id}
            selected={organization.id === user?.organizationId}
            onClick={() => choose(organization.id)}
            data-testid={`org-switcher-${testIdPart(organization.id)}-item`}
          >
            <ListItemIcon>
              {organization.id === user?.organizationId ? (
                <CheckIcon fontSize="small" />
              ) : null}
            </ListItemIcon>
            <ListItemText
              primary={organization.name}
              secondary={ROLE_LABELS[organization.role]}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
